import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import {
	usePageTreeSearchQueryQuery,
	type usePageTreeSearchQueryQuery$data,
	type usePageTreeSearchQueryQuery$variables,
} from '@confluence/pagetree-search-macro/entry-points/usePageTreeSearchQueryQuery.ts';
import {
	useSpaceValidationQueryQuery,
	type useSpaceValidationQueryQuery$data,
	type useSpaceValidationQueryQuery$variables,
} from '@confluence/pagetree-search-macro/entry-points/useSpaceValidationQueryQuery.ts';
import {
	getRootPageParam,
	parseRootPageString,
} from '@confluence/pagetree-search-macro/entry-points/pageTreeSearchMacroParams.ts';

import type {
	MacroContentNode,
	MacroPreloadHandler,
	MacroPreloadContext,
} from '../preloadMacroData.ts';

/**
 * Preload handler for the pagetree-search macro.
 *
 * If the macro has no `rootPage` parameter, no query is needed (falls back
 * to the current page as ancestor).
 *
 * If `rootPage` has a title (e.g. "SPACE:Title"), preloads the combined
 * query (space validation + page resolution in one request).
 * If `rootPage` has only an explicit space key (e.g. "SPACE:"),
 * preloads the space-only validation query.
 */
export const pagetreeSearchPreloadHandler: MacroPreloadHandler = {
	extensionKey: 'pagetreesearch',

	routes: ['view', 'space-overview'],

	preload: async (node: MacroContentNode, context: MacroPreloadContext): Promise<void> => {
		const macroParams = node.attrs?.parameters?.macroParams;
		const rootPageString = getRootPageParam(macroParams);

		if (rootPageString == null) {
			return;
		}

		const { spaceKey, title, hasExplicitSpace } = parseRootPageString(
			rootPageString,
			context.spaceKey,
		);

		if (!spaceKey) {
			return;
		}

		// Combined query: resolves page, validates space when explicitly provided
		if (title) {
			await query<usePageTreeSearchQueryQuery$data, usePageTreeSearchQueryQuery$variables>({
				query: usePageTreeSearchQueryQuery,
				variables: { spaceKey, title, validateSpace: hasExplicitSpace },
				context: { single: true },
			});
			return;
		}

		// Space-only validation (e.g. "MYSPACE:")
		if (hasExplicitSpace) {
			await query<useSpaceValidationQueryQuery$data, useSpaceValidationQueryQuery$variables>({
				query: useSpaceValidationQueryQuery,
				variables: { spaceKey },
				context: { single: true },
			});
		}
	},
};
